import { z } from 'zod';

// Browser-safe contracts of the Reservations module (zod only), and the booking rules as pure functions (shared by the
// server, which enforces them, and the app, which shows what is possible).

export const ResourceKind = z.enum(['room', 'gym', 'court', 'elevator', 'pool', 'bbq', 'guest_suite', 'other']);
export const BookingStatus = z.enum(['requested', 'confirmed', 'cancelled', 'rejected']);
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');

export const ResourceInput = z
  .object({
    name: z.string().trim().min(1).max(100),
    kind: ResourceKind,
    description: z.string().trim().max(1000).default(''),
    location: z.string().trim().max(200).default(''),
    opensAt: time.default('07:00'),
    closesAt: time.default('23:00'),
    slotMinutes: z.union([z.literal(15), z.literal(30), z.literal(60)]).default(30),
    minMinutes: z.number().int().min(15).max(24 * 60).default(30),
    maxMinutes: z.number().int().min(15).max(24 * 60).default(240),
    maxDaysAhead: z.number().int().min(1).max(365).default(60),
    minNoticeHours: z.number().int().min(0).max(720).default(2),
    cancelNoticeHours: z.number().int().min(0).max(720).default(24),
    weeklyQuota: z.number().int().min(1).max(50).nullable().default(null),
    feeCents: z.number().int().min(0).max(1_000_000).default(0),
    depositCents: z.number().int().min(0).max(1_000_000).default(0),
    requiresApproval: z.boolean().default(false),
    rulesText: z.string().trim().max(3000).default(''),
    active: z.boolean().default(true),
  })
  .refine((r) => r.closesAt > r.opensAt, { message: 'Closing time must be after opening time', path: ['closesAt'] })
  .refine((r) => r.maxMinutes >= r.minMinutes, { message: 'Maximum duration must be at least the minimum', path: ['maxMinutes'] });
export type ResourceInput = z.input<typeof ResourceInput>;

export const BookingInput = z.object({
  resourceId: z.uuid(),
  unitId: z.uuid(),
  start: z.iso.datetime({ offset: true }),
  end: z.iso.datetime({ offset: true }),
  notes: z.string().trim().max(500).default(''),
});
export type BookingInput = z.input<typeof BookingInput>;

export const BlackoutInput = z.object({ start: z.iso.datetime({ offset: true }), end: z.iso.datetime({ offset: true }), reason: z.string().trim().max(200).default('') });
export type BlackoutInput = z.input<typeof BlackoutInput>;

export interface Resource extends Omit<z.output<typeof ResourceInput>, never> {
  id: string;
  blackouts: { id: string; start: string; end: string; reason: string }[];
}

export interface Booking {
  id: string;
  resourceId: string;
  resourceName: string;
  unitId: string;
  unitLabel: string;
  start: string;
  end: string;
  status: z.infer<typeof BookingStatus>;
  feeCents: number;
  depositCents: number;
  notes: string;
  /** Booked by the caller or for one of the caller's units. */
  mine: boolean;
  /** The account that booked (to notify it). */
  bookedBy: string | null;
  bookedByName: string | null;
  createdAt: string;
}

/** A slot already taken, as others see it (no names). */
export interface TakenSlot {
  start: string;
  end: string;
  status: 'requested' | 'confirmed' | 'blackout';
  mine: boolean;
  reason?: string;
}

// --- Rules (Quebec time: the building's clock)

export const TIME_ZONE = 'America/Toronto';

/** Local date (YYYY-MM-DD), minutes since midnight and ISO weekday (1 = Monday) of an instant, in Quebec. */
export function localParts(instant: Date): { date: string; minutes: number; weekday: number } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short', hourCycle: 'h23' })
      .formatToParts(instant)
      .map((p) => [p.type, p.value]),
  );
  const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(parts.weekday!) + 1;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, minutes: Number(parts.hour) * 60 + Number(parts.minute), weekday };
}

const toMinutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));

export type RuleViolation = 'order' | 'same_day' | 'hours' | 'slot' | 'too_short' | 'too_long' | 'notice' | 'too_far' | 'blackout' | 'quota';

/**
 * Checks a booking against the facility's rules. `weekCount` is the number of active bookings of this unit for this
 * facility in the booking's week (Monday to Sunday, Quebec time), not counting this one.
 */
export function checkBooking(
  r: Pick<Resource, 'opensAt' | 'closesAt' | 'slotMinutes' | 'minMinutes' | 'maxMinutes' | 'maxDaysAhead' | 'minNoticeHours' | 'weeklyQuota' | 'blackouts'>,
  start: Date,
  end: Date,
  now: Date,
  weekCount: number,
): RuleViolation | null {
  if (end <= start) return 'order';
  const s = localParts(start);
  const e = localParts(new Date(end.getTime() - 1));
  if (s.date !== e.date) return 'same_day';
  const endMinutes = e.minutes + 1;
  if (s.minutes < toMinutes(r.opensAt) || endMinutes > toMinutes(r.closesAt)) return 'hours';
  if (s.minutes % r.slotMinutes !== 0 || endMinutes % r.slotMinutes !== 0) return 'slot';
  const minutes = (end.getTime() - start.getTime()) / 60_000;
  if (minutes < r.minMinutes) return 'too_short';
  if (minutes > r.maxMinutes) return 'too_long';
  if (start.getTime() < now.getTime() + r.minNoticeHours * 3_600_000) return 'notice';
  if (start.getTime() > now.getTime() + r.maxDaysAhead * 86_400_000) return 'too_far';
  if (r.blackouts.some((b) => Date.parse(b.start) < end.getTime() && Date.parse(b.end) > start.getTime())) return 'blackout';
  if (r.weeklyQuota !== null && weekCount >= r.weeklyQuota) return 'quota';
  return null;
}

/** Monday 00:00 to next Monday 00:00 (Quebec time) of the week containing an instant, as ISO instants. */
export function weekOf(instant: Date): { from: string; to: string } {
  const { date, weekday } = localParts(instant);
  const monday = new Date(`${date}T12:00:00Z`);
  monday.setUTCDate(monday.getUTCDate() - (weekday - 1));
  const day = (d: Date) => d.toISOString().slice(0, 10);
  const next = new Date(monday);
  next.setUTCDate(next.getUTCDate() + 7);
  return { from: localMidnight(day(monday)), to: localMidnight(day(next)) };
}

/** The instant of 00:00 Quebec time on a date (handles daylight saving). */
export function localMidnight(date: string): string {
  for (const offset of ['-04:00', '-05:00']) {
    const candidate = new Date(`${date}T00:00:00${offset}`);
    if (localParts(candidate).minutes === 0 && localParts(candidate).date === date) return candidate.toISOString();
  }
  return new Date(`${date}T00:00:00-05:00`).toISOString();
}
